import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { BILLING_STATUS, calculateCosts, createOrganisationBillingRecord, Currency, DEFAULT_CURRENCY, FREE_NODES, FREE_PROJECTS, FREE_USERS, getCurrency, getMonthForRunId, getRunIdForDate, Organisation, OrganisationBilling } from "../../common/organisation";
import { INSIGHT_VALUE_TYPE, InsightCard } from "../../ui-components/insight-cards/insight-cards.component";
import { AnalyticsData, AnalyticsService } from "../../model-services/analytics-service";
import { LineData } from "../../charts/line-chart/line-chart.component";
import { CURRENT_STATE_PROJECT_KEY } from "../../common/linqset-model";
import { SCREEN } from "../layouts-enums";
import { ORGANISATION_API, RepositoryService } from "../../linq-utils/repository.service";
import { UserService } from "../../user/user.service";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { OrganisationService } from "../../organisation/organisation.service";
import { BarData } from "../../charts/bar-chart/bar-chart.component";
import { colorAsARGB, MODEL_COLOURS } from "../../model-services/model-item-enums";
import { FUNCTION, FunctionService } from "../../model-services/function.service";

const COMPONENT = "Organisation billing"

@Component({
    selector: 'app-organisation-billing',
    templateUrl: './organisation-billing.html',
    styleUrls: ['./organisation-billing.scss'],
    standalone: false,

})
export class OrganisationBillingComponent implements OnInit, OnDestroy {
    screen:SCREEN = SCREEN.ORGANISATION_BILLING
    loading:boolean

    organisation:Organisation
    organisationKey:string
    routeSub:Subscription
    billingRecords:OrganisationBilling[]
    analyticsData:AnalyticsData
    lineData:LineData
    nodesLineData:LineData
    cards:InsightCard[]

    costCards:InsightCard[]
    
    billingChartData:BarData[]
    currentRecord:OrganisationBilling
    nextMonthRecord:OrganisationBilling
    currency:Currency

    constructor(private route:ActivatedRoute, private organisationService:OrganisationService, private analyticsService:AnalyticsService, private repoService:RepositoryService, 
        private userService:UserService, private functionService:FunctionService) {}
    
    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
    }
    
    async ngOnInit() {
        setTimeout(() => {
    
            this.routeSub = this.route.params.subscribe(async p => {
                this.organisationKey = p['organisation_key']

                await this.createAnalytics()
                this.createStatistics()        
            })
        })

    }

    async createAnalytics() {
        await this.organisationService.checkOrganisationDataForRoute(this.organisationKey)

        this.organisation = this.organisationService.organisation
        // billing records fall back to the default currency, so the prices shown must too
        this.currency = getCurrency(this.organisation.currencyCode || DEFAULT_CURRENCY)

        this.analyticsData = await this.analyticsService.loadAnalyticsData(this.organisationKey)
        this.billingRecords = await this.repoService.callSingleAPI(this.repoService.getData(ORGANISATION_API(this.organisation._key) + "/billing/runs"), COMPONENT, "Select run", this.userService.getUser())

        this.billingRecords = this.billingRecords.sort((a,b) => a.runId > b.runId?1:-1)

        const data = this.analyticsData && this.analyticsService.loadAnalyticsDataForChart(this.analyticsData.analytics)
        this.lineData = data.changes
        this.nodesLineData = data.nodes
        this.refreshProjection()
        this.billingRecords.push(this.nextMonthRecord)

        this.billingChartData = this.billingRecords.map(br => {
            if (br._key === this.nextMonthRecord._key)
                return new BarData("next-month", "Predicted bill", [br.nodes.amount, br.projects.amount, br.users.amount], [this.getComparisonColour(MODEL_COLOURS.BILLING_NODES), this.getComparisonColour(MODEL_COLOURS.BILLING_PROJECTS), this.getComparisonColour(MODEL_COLOURS.BILLING_USERS)], [this.getComparisonColour(MODEL_COLOURS.BILLING_HOVER), this.getComparisonColour(MODEL_COLOURS.BILLING_HOVER), this.getComparisonColour(MODEL_COLOURS.BILLING_HOVER)])
            else 
                return new BarData(br.runId.toString(), getMonthForRunId(br.runId), [br.nodes.amount, br.projects.amount, br.users.amount], [MODEL_COLOURS.BILLING_NODES, MODEL_COLOURS.BILLING_PROJECTS, MODEL_COLOURS.BILLING_USERS], [MODEL_COLOURS.BILLING_HOVER, MODEL_COLOURS.BILLING_HOVER, MODEL_COLOURS.BILLING_HOVER])
        })

        
    }

    getComparisonColour(hexcolour:string) {
        const rgb = colorAsARGB(hexcolour)
        return `rgba(${rgb.r},${rgb.g},${rgb.b},0.4)`
    }

    public async handleFunctionEvent(event:{function:FUNCTION, value?:any}) {
        try {
            this.loading = true
            await this.functionService.executeFunction(event.function, event.value)
        }catch(err) {
            this.loading = false
            console.error(err)
        }finally {
            this.loading = false
        }
    }
    
    createStatistics() {
        this.cards = []

        const numModels = this.organisation.models.filter(m => !m.deletedDate).reduce((t, m) => {
            if (m.projectKey !== CURRENT_STATE_PROJECT_KEY) t.projects++
            else {
                t.projects += m.projects?m.projects.length:0
                t.currentState++
            }
            return t
        }, {currentState: 0, projects:0})

        const latest = this.analyticsData.analytics.length>0 && this.analyticsData.analytics[this.analyticsData.analytics.length-1]

        const csModelsCard = new InsightCard("Current state models", null, 'Number of current state models', null, numModels.currentState)
        this.cards.push(csModelsCard);

        const projectModelsCard = new InsightCard("Project models", null, 'Number of project models', null, numModels.projects)
        this.cards.push(projectModelsCard);

        const projectsCard = new InsightCard("Projects", null, 'Number of projects', null, this.organisation.projects.length)
        this.cards.push(projectsCard);

        if (latest) {
            const nodesCard = new InsightCard("Current state nodes", null, 'Number of current state nodes', null, latest.nodes)
            this.cards.push(nodesCard);

            const projectNodesCard = new InsightCard("Project nodes", null, 'Number of project nodes', null, latest.projectNodes)
            this.cards.push(projectNodesCard);

            const backupNodesCard = new InsightCard("Backup nodes", null, 'Number of backup nodes', null, latest.backupNodes)
            this.cards.push(backupNodesCard);
        }

        const orphanedNodesCard = new InsightCard("Orphaned nodes", null, 'Number of nodes without a model or project', null, this.analyticsData.orphanedNodes)
        this.cards.push(orphanedNodesCard);

        const orphanedEdgesCard = new InsightCard("Orphaned edges", null, 'Number of edges without a model or project', null, this.analyticsData.orphanedEdges)
        this.cards.push(orphanedEdgesCard);

        const usersCard = new InsightCard("Users", null, 'Number of users', null, this.organisation.numUsers)
        this.cards.push(usersCard);

        //const adminsCard = new InsightCard("Admin users", null, 'Number of admin users', null, this.organisation.numAdmins)
        //this.cards.push(adminsCard);

        this.costCards = []

        if (latest) {                      
            const nodeAmountCard = new InsightCard(`Nodes cost (${this.currency.nodePrice} each above ${FREE_NODES} nodes)`, null, 'Current monthly cost for nodes', null, this.currentRecord.nodes.amount, undefined, INSIGHT_VALUE_TYPE.CURRENCY)
            nodeAmountCard.icon = "graph_1"
            this.costCards.push(nodeAmountCard);

            const projectAmountCard = new InsightCard(`Projects cost (${this.currency.projectPrice} each above ${FREE_PROJECTS} project)`, null, 'Current monthly cost for projects', null, this.currentRecord.projects.amount, undefined, INSIGHT_VALUE_TYPE.CURRENCY)
            projectAmountCard.icon = "cases"
            this.costCards.push(projectAmountCard);

            const userAmountCard = new InsightCard(`Users cost (${this.currency.userPrice} each above ${FREE_USERS} user)`, null, 'Current monthly cost for users', null, this.currentRecord.users.amount, undefined, INSIGHT_VALUE_TYPE.CURRENCY)
            userAmountCard.icon = "group"
            this.costCards.push(userAmountCard);                
        }
    }

    refreshProjection() {
        const now = new Date()
        
        const next = new Date(now.getFullYear(), now.getMonth()+1, 1)
        const runId = getRunIdForDate(next)

        const ma = this.analyticsData.analytics[this.analyticsData.analytics.length-1]
        this.currentRecord = createOrganisationBillingRecord(this.organisation, next, next, runId, ma)

        const totalNodes = this.nodesLineData.lineMap["Total nodes"]
        const dailyNodeChange = (totalNodes[totalNodes.length-1] - totalNodes[0])/totalNodes.length
        // days left in this month (day 0 of next month is the last day of this one)
        const numDays = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate() - now.getDate()

        this.nextMonthRecord = createOrganisationBillingRecord(this.organisation, next, next, runId, ma)       
        this.nextMonthRecord.status = BILLING_STATUS.PREDICTION
        this.nextMonthRecord.nodes.number += numDays*dailyNodeChange
        this.nextMonthRecord.nodes.number = Math.round(this.nextMonthRecord.nodes.number)
        calculateCosts(this.nextMonthRecord)

        this.handleProjectedChange()
    }

    handleProjectedChange() {
        calculateCosts(this.nextMonthRecord)
    }

    hasCharges() {
        return this.billingRecords?.some(br => br.nodes.amount + br.projects.amount + br.users.amount > 0)
    }

    editOrganisation() {
        const dialogRef = this.organisationService.openOrganisationDialog(this.organisationService.organisation)
            
        dialogRef.afterClosed().subscribe(async result => {
            if (result) {
                await this.organisationService.saveOrganisation(result, false)
            }
        })
    }
}